import { useParams } from "react-router-dom";
import { useCreateCustomReportContext } from "../../pages/reports/context/createCustomReport.context";
import {
  useRunCustomReportQuery,
  useRunReportQuery,
} from "../../api/network/reportApiServices";
import { APP_CONFIG, REPORT_TYPES } from "../../constants/constants";
import { TListingQueryParams } from "../../api/store/commonSlice";
import { useSelector } from "react-redux";
import { TReportField } from "../../api/types/Report";
import { useTranslation } from "react-i18next";
import useConversion from "../../hooks/conversion";
import { REPORT_FIELD_OUTPUT_TYPES } from "../../pages/reports/constants";
import { useEffect, useState } from "react";
import { localStorageKeys } from "../../utils/localStorageItems";
import { AGGREGATE_FUNCTION } from "../../pages/reports/components/popupReportColumnAggregation";

const ReportTableRowGrandTotal = () => {
  const {
    reportType,
    categoryId,
    reportId,
    reportFilters: urlParamReportFilters,
  } = useParams<{
    reportType: string;
    categoryId: string;
    reportId: string;
    reportFilters: string;
  }>();
  const { t: tReports } = useTranslation("translation", {
    keyPrefix: "reports",
  });
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { reports: orgReportQueryParams } = listingQueryParams;

  const {
    runReportParams,
    skipRunReport,
    specificReportResponse,
    columnsVisibility,
  } = useCreateCustomReportContext();

  // Columns
  const [savedColumns, setSavedColumns] = useState<string[] | undefined>(
    undefined
  );

  const reportVisibleColumns: { [key: string]: string[] } = useSelector(
    (state: any) => state.commonReducer.reportVisibleColumns
  );
  useEffect(() => {
    const reportColumnsKey = localStorageKeys.columns.Reports(
      reportType ?? "",
      categoryId ?? "",
      reportId ?? ""
    );
    setSavedColumns(
      reportVisibleColumns[reportColumnsKey] // || getLocalStorageItem(reportColumnsKey)
    );
  }, [reportId, categoryId, reportType, reportVisibleColumns]);

  const { convertToUnit, DisplayConvertedValue } = useConversion();

  const {
    data: reportData,
    isFetching,
    isError,
    error: reportError,
    isUninitialized,
  } = (
    reportType === REPORT_TYPES.STANDARD
      ? useRunReportQuery
      : useRunCustomReportQuery
  )(
    {
      ...runReportParams,
      // remove `is_group_by` key from the query params
      report_fields:
        runReportParams?.report_fields?.map((field: TReportField) => ({
          ...field,
          is_group_by: false,
        })) || [],
      page: 1, // page always stays one for grand total row (no need to call another API on page change)
    },
    {
      skip:
        skipRunReport || // skipRunReport is set to true when multiple states (columnsSearchKeyword and page) are changing - prevents multiple API calls
        !runReportParams.id ||
        (reportType !== REPORT_TYPES.STANDARD
          ? !orgReportQueryParams ||
            orgReportQueryParams.id === "" ||
            !specificReportResponse?.report_fields ||
            !specificReportResponse
          : !orgReportQueryParams ||
            !orgReportQueryParams.id ||
            orgReportQueryParams.id === "" ||
            !specificReportResponse),
    }
  );

  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  useEffect(() => {
    if (savedColumns) {
      setVisibleColumns(savedColumns);
    } else {
      setVisibleColumns(
        columnsVisibility.filter((col) => col.show).map((col) => col.name)
      );
    }
  }, [savedColumns, columnsVisibility]);

  if (isUninitialized || isFetching || isError || !reportData) return null;

  return (
    <tr>
      {/* Index Column */}
      <td>&nbsp;</td>

      {/* Grand Total Row Cells */}
      {specificReportResponse?.report_fields?.map(
        (field: TReportField, field_index: number) => {
          // const this_field_config =
          //   specificReportResponse?.report_fields?.[field_index];
          const this_field_header = visibleColumns[field_index];
          const this_field_config = specificReportResponse?.report_fields?.find(
            (field) => field.field.name === this_field_header
          );

          const grandTotal = !!this_field_config?.aggregate_function
            ? this_field_config?.aggregate_function !== AGGREGATE_FUNCTION.COUNT
              ? convertToUnit(
                  this_field_config?.output_type as REPORT_FIELD_OUTPUT_TYPES,
                  Number(
                    reportData?.results?.[0]?.[
                      (this_field_config as TReportField)?.identifier
                    ]
                  ),
                  this_field_config?.convert_to_whole_number
                )
              : {
                  finalData:
                    reportData?.results?.[0]?.[
                      (this_field_config as TReportField)?.identifier
                    ],
                  isConverted: false,
                }
            : null;

          if (
            (field_index > 0 &&
              columnsVisibility.find(
                (col) => col.name === this_field_config?.field.name
              )?.show === false) ||
            !this_field_config?.field.name // adding this line fixes the issue with table layout when showing/hiding columns
            // !!this_field_config?.field.name &&
            // !visibleColumns.includes(this_field_config?.field.name)
          ) {
            return null;
          }

          return (
            <td
              key={`grand-total-cell-${field.id}`}
              className="px-4 py-3 whitespace-nowrap text-xs font-medium leading-6 text-accent-blue-dark border-t border-gray-200"
            >
              {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                <>
                  name:{this_field_config?.field.name}
                  <br />
                  aggre:{this_field_config?.aggregate_function}
                </>
              )}
              {field_index === 0 && (
                <strong>
                  {tReports("grand_total")}
                  <>&nbsp;</>
                </strong>
              )}
              {!!this_field_config?.aggregate_function ? (
                <span className="font-bold">
                  {!!grandTotal && (
                    <DisplayConvertedValue
                      convertedCellData={grandTotal}
                      originalData={
                        reportData?.results?.[0]?.[field.identifier]
                      }
                    />
                  )}
                </span>
              ) : (
                <>&nbsp;</>
              )}
            </td>
          );
        }
      )}
    </tr>
  );
};
export default ReportTableRowGrandTotal;
